// Hospital Configuration Service - Centralized dynamic configuration
import axios from 'axios';
import { API_BASE_URL as RAW_API_BASE_URL } from './api';

// Types for configuration structure
export interface HospitalConfig {
  site_title: string;
  site_title_short: string;
  site_abbreviation: string;
  tagline: string;
  enable_popup_ai_chat_button: boolean;
  country_emergency_number: string;
  page_titles: {
    login: string;
    dashboard: string;
    messages: string;
    settings: string;
    admin: string;
    patient: string;
    staff: string;
    ai_chat: string;
    notifications: string;
  };
  branding: {
    primary_color: string;
    secondary_color: string;
    accent_color: string;
  };
  hospital_name: string;
  hospital_code: string;
  hospital_email: string;
  hospital_phone: string;
  hospital_website: string;
  landing_page: {
    hero: {
      title: string;
      subtitle: string;
      stats: {
        years_of_excellence: string;
        patients_served_annually: string;
        healthcare_professionals: string;
        medical_specialties: string;
      };
    };
    about: {
      title: string;
      subtitle: string;
    };
    contact: {
      emergency: {
        phone: string;
        phone_display: string;
        hours: string;
      };
      general: {
        phone: string;
        phone_display: string;
        hours: string;
      };
      billing: {
        phone: string;
        phone_display: string;
        hours: string;
      };
    };
    address: {
      street: string;
      city: string;
      state: string;
      zip_code: string;
      country: string;
      full_address: string;
    };
    facility: {
      description: string;
      features: string[];
    };
    seo: {
      meta_description: string;
      meta_keywords: string;
    };
  };
}

class ConfigService {
  private config: HospitalConfig | null = null;
  private loading = false;
  private listeners: ((config: HospitalConfig) => void)[] = [];

  // API endpoint - use same base as existing apiService
  private readonly API_URL = RAW_API_BASE_URL.replace(/\/$/, '');
  
  /**
   * Get the current configuration, fetch if not already cached
   */
  async getConfig(): Promise<HospitalConfig> {
    // Use cache if available, otherwise fetch
    if (this.config) {
      return this.config;
    }
    return this.fetchConfig();
  }

  /**
   * Fetch configuration from API
   */
  private async fetchConfig(): Promise<HospitalConfig> {
    this.loading = true;
    
    try {
      // Add cache-busting timestamp to prevent browser caching
      const timestamp = new Date().getTime();
      const response = await axios.get(`${this.API_URL}/site-config/?t=${timestamp}`, {
        timeout: 10000, // 10 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });

      this.config = response.data;
      this.loading = false;

      // Notify all listeners
      this.listeners.forEach(listener => listener(this.config!));

      return this.config!
    } catch (error) {
      this.loading = false;
      console.error('Failed to fetch hospital configuration:', error);
      
      // Return fallback configuration
      const fallbackConfig: HospitalConfig = {
        site_title: 'Aimepic OneCare',
        site_title_short: 'OneCare',
        site_abbreviation: 'OneCare',
        tagline: 'Healthcare Operations and Management Platform',
        enable_popup_ai_chat_button: false,
        country_emergency_number: '911',
        page_titles: {
          login: 'Login - OneCare',
          dashboard: 'Dashboard - OneCare',
          messages: 'Messages - OneCare',
          settings: 'Settings - OneCare',
          admin: 'Admin Panel - OneCare',
          patient: 'Patient Portal - OneCare',
          staff: 'Staff Dashboard - OneCare',
          ai_chat: 'AI Assistant - OneCare',
          notifications: 'Notifications - OneCare',
        },
        branding: {
          primary_color: '#2563eb',
          secondary_color: '#059669',
          accent_color: '#dc2626',
        },
        hospital_name: 'Aimepic OneCare',
        hospital_code: 'OneCare',
        hospital_email: 'contact-us@aimepic.com',
        hospital_phone: '+15551234567',
        hospital_website: 'https://www.aimepic.com',
        landing_page: {
          hero: {
            title: 'Aimepic OneCare',
            subtitle: 'Healthcare Operations and Management Platform - Streamline your healthcare operations with our comprehensive management solution.',
            stats: {
              years_of_excellence: '20+',
              patients_served_annually: '50,000+',
              healthcare_professionals: '500+',
              medical_specialties: '25+',
            }
          },
          about: {
            title: 'About Aimepic OneCare',
            subtitle: 'A comprehensive healthcare operations and management platform designed to streamline workflows, enhance communication, and improve patient care delivery.',
          },
          contact: {
            emergency: {
              phone: '+15559112273',
              phone_display: '(555) 911-CARE',
              hours: 'Available 24/7 for medical emergencies'
            },
            general: {
              phone: '+15551234567',
              phone_display: '(555) 123-4567',
              hours: 'Mon-Fri 8AM-6PM'
            },
            billing: {
              phone: '+15551234567',
              phone_display: '(555) 123-4567 ext. 2',
              hours: 'Monday-Friday, 8AM-5PM'
            }
          },
          address: {
            street: '123 Healthcare Blvd',
            city: 'Medical City',
            state: 'MC',
            zip_code: '12345',
            country: 'US',
            full_address: '123 Healthcare Blvd, Medical City, MC 12345'
          },
          facility: {
            description: 'Our state-of-the-art main facility offers comprehensive medical services with advanced technology and compassionate care.',
            features: [
              '300-bed acute care facility',
              'Level 1 Trauma Center',
              'Advanced surgical suites',
              'Comprehensive imaging center',
              'On-site pharmacy',
              'Ample parking available'
            ]
          },
          seo: {
            meta_description: 'Professional healthcare services with 24/7 emergency care, board-certified physicians, and state-of-the-art medical technology. Serving our community for 20+ years.',
            meta_keywords: 'healthcare services, emergency care, digital health, patient portal, medical professionals, HIPAA-ready'
          }
        }
      };

      this.config = fallbackConfig;
      return fallbackConfig;
    }
  }

  /**
   * Subscribe to configuration changes
   */
  subscribe(listener: (config: HospitalConfig) => void): () => void {
    this.listeners.push(listener);
    
    // Immediately call with current config if available
    if (this.config) {
      listener(this.config);
    }

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Force refresh configuration from server
   */
  async refresh(): Promise<HospitalConfig> {
    this.config = null;
    return this.fetchConfig();
  }

  /**
   * Clear cached configuration (for testing/development)
   */
  clearCache(): void {
    this.config = null;
  }

  /**
   * Get emergency contact info specifically
   */
  async getEmergencyContact(): Promise<{ phone: string; display: string; hours: string }> {
    const config = await this.getConfig();
    return {
      phone: config.landing_page.contact.emergency.phone,
      display: config.landing_page.contact.emergency.phone_display,
      hours: config.landing_page.contact.emergency.hours
    };
  }

  /**
   * Get general contact info specifically
   */
  async getGeneralContact(): Promise<{ phone: string; display: string; hours: string }> {
    const config = await this.getConfig();
    return {
      phone: config.landing_page.contact.general.phone,
      display: config.landing_page.contact.general.phone_display,
      hours: config.landing_page.contact.general.hours
    };
  }

  /**
   * Get full address formatted
   */
  async getAddress(): Promise<string> {
    const config = await this.getConfig();
    return config.landing_page.address.full_address;
  }

  /**
   * Get branding colors
   */
  async getBranding(): Promise<{ primary: string; secondary: string; accent: string }> {
    const config = await this.getConfig();
    return {
      primary: config.branding.primary_color,
      secondary: config.branding.secondary_color,
      accent: config.branding.accent_color
    };
  }
}

// Export singleton instance
export const configService = new ConfigService();

// Export default
export default configService;